import React, { useEffect, useState, useRef } from "react";

interface ImageSliderProps {
  bookID: string;
  tuneNumber: string;
  promptNumber: string;
  kidName: string;
}

export default function ImageSlider({ bookID, tuneNumber, promptNumber, kidName }: ImageSliderProps) {
  const [image, setImage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const tuneBookIdPromptNumber = `${tuneNumber}_${bookID}_${promptNumber}`;
  const maxRetries = 6;
  const [retryCount, setRetryCount] = useState(0);

  // Simple function to send height to parent
  const sendHeightToParent = () => {
    const height = document.body.scrollHeight;
    if (window.parent && window.parent !== window) {
      window.parent.postMessage({
        height: height
      }, '*');
    }
  };
  
  // Handle window resize
  useEffect(() => {
    window.addEventListener('resize', sendHeightToParent);
    return () => window.removeEventListener('resize', sendHeightToParent);
  }, []);

  useEffect(() => {
    const fetchImages = async () => {
      try {
        console.log(`Fetching images... (Attempt ${retryCount + 1})`);
        const response = await fetch(
          `https://giftomagic.com/api/images?tune_book_id_prompt_number=${tuneBookIdPromptNumber}`
        );
        const data = await response.json();

        if (data.images && data.images.length > 0) {
          console.log("Setting image for prompt", Number(promptNumber)+1)
          setImage(data.images[0]);
          setLoading(false);
          // Send height after image loads
          setTimeout(sendHeightToParent, 100);
        }
        
        if (!data.images || data.images.length === 0) {
          console.log("Image generation requested. Waiting before retrying... prompt is", Number(promptNumber)+1);
          setTimeout(() => setRetryCount((prev) => prev + 1), 10000);
        }
      } catch (error) {
        console.log(JSON.stringify({ message: "Error fetching images", error: error }));
      }
    };

    if (retryCount < maxRetries) {
      fetchImages();
    } else {
      console.error("Max retries reached. Images not found.");
      setLoading(false);
    }
  }, [retryCount, tuneBookIdPromptNumber, bookID, promptNumber, tuneNumber, kidName]);
  return (
    <div className="fixed inset-0 flex justify-center items-center bg-black">
      {loading ? (
        <div className="flex justify-center items-center w-full" style={{ maxWidth: '100%' }}>
          <div
            className="flex justify-center items-center"
            style={{
              width: '350px',
              height: '516px',
              minWidth: '350px',
              minHeight: '496px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              border: '1px solid #ccc', // Add border
              borderRadius: '10px', // Add 10px radius
              marginBottom: '20px',
              marginLeft: 'auto',
              marginRight: 'auto',
            }}
          >
            <BookBuildingSpinner />
          </div>
        </div>
      ) : image ? (
        <div className="flex justify-center items-center w-full" style={{ maxWidth: '100%' }}>
          <img
            src={image}
            alt={`Generated image for prompt ${promptNumber}`}
            className="rounded-lg shadow-lg mx-auto block"
            style={{
              width: '350px',
              height: '496px',
              objectFit: 'contain',
              display: 'block',
              marginLeft: 'auto',
              marginRight: 'auto',
              marginBottom: '20px'
            }}
            onLoad={sendHeightToParent}
          />
        </div>
      ) : (
        <p className="text-center text-lg font-semibold">No image found.</p>
      )}
    </div>
  );
}

const BookBuildingSpinner = () => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setProgress(prev => prev >= 100 ? 100 : prev + 1);
    }, 900); // Update every 900ms (90 seconds / 100 steps)

    return () => clearInterval(timer);
  }, []);

  const containerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center'
  };

  const wrapperStyle: React.CSSProperties = {
    position: 'relative',
    width: '30px',
    height: '30px',
    marginBottom: '16px'
  };

  const backgroundCircleStyle: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: '50%',
    border: '2px solid rgba(255, 255, 255, 0.3)'
  };

  const svgStyle: React.CSSProperties = {
    width: '30px',
    height: '30px',
    transform: 'rotate(-90deg)'
  };

  const centerDotContainerStyle: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  };

  const centerDotStyle: React.CSSProperties = {
    width: '4px',
    height: '4px',
    backgroundColor: 'white',
    borderRadius: '50%'
  };

  const textStyle: React.CSSProperties = {
    fontSize: '14px',
    fontWeight: 600,
    color: 'white',
    textAlign: 'center'
  };

  return (
    <div style={containerStyle}>
      <div style={{ textAlign: 'center', marginBottom: '16px' }}>
        <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#FFFFFF' }}>
          Turning your kid into a superhero!
        </span>
      </div>
      <div style={wrapperStyle}>
        {/* Background circle */}
        <div style={backgroundCircleStyle}></div>
        
        {/* Progress circle */}
        <svg style={svgStyle} viewBox="0 0 30 30">
          <circle
            cx="15"
            cy="15"
            r="12"
            fill="none"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            strokeDasharray={`${2 * Math.PI * 12}`}
            strokeDashoffset={`${2 * Math.PI * 12 * (1 - progress / 100)}`}
          />
        </svg>
         
        {/* Center dot */}
        <div style={centerDotContainerStyle}>
          <div style={centerDotStyle}></div>
        </div>
      </div>
      <span style={textStyle}>
        {Math.round(progress)}%
      </span>
    </div>
  );
};